import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { GenerateCollectionDTO } from '../types/collection';
import { CollectionListItem } from '../types/collection';
import { processFileForUpload } from '../utils/pdfExtractor';
import '../styles/CreateCollectionModal.css';

interface CreateCollectionModalProps {
  onClose: () => void;
  onCreate: (name: string, description: string, baseCollectionId?: string, leetcodeNumbers?: number[]) => void;
  onGenerateByAI?: (data: GenerateCollectionDTO) => void;
  collections: CollectionListItem[];
  setIsGeneratingAI: (value: boolean) => void;
}

export const CreateCollectionModal: React.FC<CreateCollectionModalProps> = ({
  onClose,
  onCreate,
  onGenerateByAI,
  collections,
  setIsGeneratingAI
}) => {
  const [activeTab, setActiveTab] = useState<'manual' | 'ai'>('manual');
  
  // Manual tab fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [baseCollection, setBaseCollection] = useState('');
  const [leetcodeNumbers, setLeetcodeNumbers] = useState('');
  
  // AI tab fields
  const [skillLevel, setSkillLevel] = useState<'beginner' | 'intermediate' | 'advanced'>('beginner');
  const [practiceTimeframe, setPracticeTimeframe] = useState('4');
  const [targetCompanies, setTargetCompanies] = useState('');
  const [focusTopics, setFocusTopics] = useState('');
  const [aiCollectionName, setAiCollectionName] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [addMoreProblems, setAddMoreProblems] = useState(false);
  const [customInstructions, setCustomInstructions] = useState('');
  
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-sync addMoreProblems checkbox with custom instructions
  useEffect(() => {
    // If custom instructions contain "only", automatically uncheck addMoreProblems
    if (customInstructions?.toLowerCase().includes('only')) {
      setAddMoreProblems(false);
    }
  }, [customInstructions]);

  // Programmatically detect if content overflows and update scroll hint visibility
  useEffect(() => {
    // Use separate storage keys for each tab
    const SCROLL_HINT_STORAGE_KEY = `aiprep_scroll_hint_dismissed_${activeTab}`;
    const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
    
    // Flag to prevent saving dismissal when programmatically resetting scroll
    let isResettingScroll = false;
    
    const checkOverflow = (isUserScroll = false) => {
      if (!modalBodyRef.current) return;
      
      const modalBody = modalBodyRef.current;
      const { scrollHeight, clientHeight, scrollTop } = modalBody;
      
      // Check if content overflows (scrollable)
      const hasOverflow = scrollHeight > clientHeight;
      
      // Check if 30 days have passed since last dismissal for this specific tab
      const lastDismissed = localStorage.getItem(SCROLL_HINT_STORAGE_KEY);
      let shouldShowBasedOnTime = true;
      
      if (lastDismissed) {
        const dismissedTime = parseInt(lastDismissed, 10);
        const currentTime = Date.now();
        shouldShowBasedOnTime = (currentTime - dismissedTime) >= THIRTY_DAYS_MS;
      }
      
      // Show hint only if:
      // 1. Content overflows
      // 2. User hasn't scrolled yet
      // 3. Either never dismissed or 30 days have passed for this specific tab
      setShowScrollHint(hasOverflow && scrollTop === 0 && shouldShowBasedOnTime);
      
      // Only save dismissal if user actually scrolled (not programmatic reset)
      if (scrollTop > 0 && hasOverflow && isUserScroll && !isResettingScroll) {
        localStorage.setItem(SCROLL_HINT_STORAGE_KEY, Date.now().toString());
      }
    };

    const handleScroll = () => {
      checkOverflow(true); // Mark as user scroll
    };

    const handleResize = () => {
      checkOverflow(false); // Not a user scroll
    };

    // Initial check
    checkOverflow(false);
    
    // Reset scroll position when switching tabs to ensure hint can appear
    const modalBody = modalBodyRef.current;
    if (modalBody) {
      isResettingScroll = true;
      modalBody.scrollTop = 0;
      // Allow a brief moment for the scroll event to fire before clearing flag
      setTimeout(() => {
        isResettingScroll = false;
      }, 50);
    }
    
    // Check on tab change (with a small delay to ensure content is rendered)
    setTimeout(() => checkOverflow(false), 100);

    if (modalBody) {
      modalBody.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleResize);
      
      // Use ResizeObserver to detect content changes
      const resizeObserver = new ResizeObserver(() => {
        checkOverflow();
      });
      resizeObserver.observe(modalBody);
      
      return () => {
        modalBody.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleResize);
        resizeObserver.disconnect();
      };
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [activeTab]);

  const parseLeetcodeNumbers = (input: string): number[] => {
    return input
      .split(/[,\s]+/)
      .map(num => parseInt(num.trim()))
      .filter(num => !isNaN(num) && num > 0);
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      alert('Please enter a collection name');
      return;
    }

    const numbers = parseLeetcodeNumbers(leetcodeNumbers);
    onCreate(
      name.trim(),
      description.trim(),
      baseCollection || undefined,
      numbers.length > 0 ? numbers : undefined
    );
    onClose();
  };

  const handleAISubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!onGenerateByAI) return;

    onClose();
    // ✅ Don't set loading state here - let parent handler control it after auth check

    try {
      let documentsContent: { filename: string; content: string; type: string; encoding?: 'plain' | 'base64' }[] = [];
      
      if (uploadedFiles.length > 0) {
        for (const file of uploadedFiles) {
          try {
            // Process file - extract text from PDF or use CSV as-is
            const processed = await processFileForUpload(file);
            
            // Send extracted text content with explicit encoding
            documentsContent.push({
              filename: file.name,
              content: processed.content,
              type: processed.type, // 'csv', 'pdf', or 'pdf-binary'
              encoding: processed.encoding // 'plain' or 'base64'
            });
          } catch (error) {
            console.error(`Failed to process ${file.name}:`, error);
            setUploadStatus(`Failed to process ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
            // ✅ Don't control loading state - parent handler will manage it
            return;
          }
        }
      }

      // Map skill levels to backend expectations
      const levelMap = {
        'beginner': 'newbie',
        'intermediate': 'good',
        'advanced': 'expert'
      } as const;

      const generateDTO: GenerateCollectionDTO = {
        name: aiCollectionName.trim() || `AI Collection - ${new Date().toLocaleDateString()}`,
        description: `AI-generated collection for ${skillLevel} level, ${practiceTimeframe} weeks practice`,
        level: levelMap[skillLevel],
        timeframeWeeks: parseInt(practiceTimeframe),
        targetCompanies: targetCompanies ? targetCompanies.split(',').map(c => c.trim()).filter(c => c) : undefined,
        focusTopics: focusTopics ? focusTopics.split(',').map(t => t.trim()).filter(t => t) : undefined,
        documents: documentsContent.length > 0 ? documentsContent : undefined,
        addMoreProblems: addMoreProblems,
        customInstructions: customInstructions.trim() || undefined
      };

      onGenerateByAI(generateDTO);
    } catch (error) {
      console.error('Failed to process files:', error);
      // ✅ Don't control loading state - parent handler will manage it
      alert('Failed to process files. Please try again.');
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const validFiles: File[] = [];
    const maxFiles = 5;
    const maxSize = 5 * 1024 * 1024; // 5MB

    for (let i = 0; i < files.length && validFiles.length < maxFiles; i++) {
      const file = files[i];
      
      const allowedTypes = [
        'application/pdf',
        'text/csv'
      ];
      
      // Check file extension as a fallback for CSV files (sometimes MIME type is text/plain)
      const fileExtension = file.name.toLowerCase().split('.').pop();
      const isValidExtension = fileExtension === 'csv' || fileExtension === 'pdf';
      
      if (!allowedTypes.includes(file.type) && !isValidExtension) {
        setUploadStatus(`File ${file.name} is not supported. Only CSV and PDF files are allowed.`);
        continue;
      }
      
      if (file.size > maxSize) {
        setUploadStatus(`File ${file.name} exceeds 5MB limit.`);
        continue;
      }
      
      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setUploadedFiles(validFiles);
      setUploadStatus('');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files && fileInputRef.current) {
      fileInputRef.current.files = files;
      handleFileUpload({ target: { files } } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    setUploadStatus('');
  };

  return ReactDOM.createPortal(
    <div className="ccm-backdrop" onClick={onClose}>
      <div className="ccm-modal" onClick={e => e.stopPropagation()}>
        <div className="ccm-header">
          <h2 className="ccm-title">Create My Collection</h2>
          <p className="ccm-subtitle">Build a custom problem set or let AI generate one for you</p>
        </div>

        <div className="ccm-body" ref={modalBodyRef}>
          {showScrollHint && (
            <div id="scroll-indicator" className="ccm-scroll-hint-floating">
              <span className="ccm-scroll-icon">👇</span>
              <span>Scroll down for more</span>
            </div>
          )}
          <div className="ccm-tabs-container">
            <div className="ccm-tabs">
              <button
                className={`ccm-tab ${activeTab === 'manual' ? 'active' : ''}`}
                onClick={() => setActiveTab('manual')}
                type="button"
              >
                <span className="ccm-tab-icon">✏️</span>
                <span>Manual Selection</span>
              </button>
              <button
                className={`ccm-tab ${activeTab === 'ai' ? 'active' : ''}`}
                onClick={() => setActiveTab('ai')}
                type="button"
              >
                <span className="ccm-tab-icon">✨</span>
                <span>AI Generated</span>
                <span className="ccm-badge">NEW</span>
              </button>
            </div>
          </div>

          {activeTab === 'manual' ? (
            <form className="ccm-form" onSubmit={handleManualSubmit}>
              <div className="ccm-form-group">
                <label className="ccm-label">
                  Collection Name
                  <span className="ccm-required">*</span>
                </label>
                <input
                  type="text"
                  className="ccm-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Graph Algorithms"
                  required
                />
              </div>

              <div className="ccm-form-group">
                <label className="ccm-label">
                  Description
                  <span className="ccm-optional">Optional</span>
                </label>
                <textarea
                  className="ccm-textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what this collection is about..."
                  rows={3}
                />
              </div>

              <div className="ccm-form-group">
                <label className="ccm-label">Base Collection</label>
                <select
                  className="ccm-select"
                  value={baseCollection}
                  onChange={(e) => setBaseCollection(e.target.value)}
                >
                  <option value="">None - Start from scratch</option>
                  {collections.map(item => (
                    <option key={item.collection.id} value={item.collection.id}>
                      {item.collection.name} ({item.collection.stats?.totalProblems || 0} problems)
                    </option>
                  ))}
                </select>
                <p className="ccm-help-text">Start with a curated set and customize from there</p>
              </div>

              <div className="ccm-form-group">
                <label className="ccm-label">
                  Add LeetCode Problems
                  <span className="ccm-optional">Optional</span>
                </label>
                <textarea
                  className="ccm-textarea ccm-leetcode-input"
                  value={leetcodeNumbers}
                  onChange={(e) => setLeetcodeNumbers(e.target.value)}
                  placeholder="Enter LeetCode numbers separated by commas or spaces
e.g., 323, 12, 35, 100"
                  rows={4}
                />
              </div>
            </form>
          ) : (
            <form className="ccm-form" onSubmit={handleAISubmit}>
              <div className="ccm-ai-intro">
                <div className="ccm-ai-intro-header">
                  <div className="ccm-ai-icon">🤖</div>
                  <div className="ccm-ai-intro-title">Powered by AI</div>
                </div>
                <div className="ccm-ai-intro-text">
                  Answer a few questions and our AI will create a personalized problem set tailored to your interview prep goals.
                </div>
              </div>

              <div className="ccm-form-group">
                <label className="ccm-label">
                  Collection Name
                  <span className="ccm-auto-badge">Auto-generated</span>
                </label>
                <input
                  type="text"
                  className="ccm-input"
                  value={aiCollectionName}
                  onChange={(e) => setAiCollectionName(e.target.value)}
                  placeholder="Leave blank for AI suggestion"
                />
                <p className="ccm-help-text">Give your collection a custom name or let AI generate one</p>
              </div>

              <div className="ccm-form-group">
                <label className="ccm-label">
                  Your Skill Level
                  <span className="ccm-required">*</span>
                </label>
                <div className="ccm-level-options">
                  <label className={`ccm-level-option ${skillLevel === 'beginner' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="skillLevel"
                      value="beginner"
                      checked={skillLevel === 'beginner'}
                      onChange={() => setSkillLevel('beginner')}
                    />
                    <span className="ccm-level-label">Beginner</span>
                  </label>
                  <label className={`ccm-level-option ${skillLevel === 'intermediate' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="skillLevel"
                      value="intermediate"
                      checked={skillLevel === 'intermediate'}
                      onChange={() => setSkillLevel('intermediate')}
                    />
                    <span className="ccm-level-label">Intermediate</span>
                  </label>
                  <label className={`ccm-level-option ${skillLevel === 'advanced' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="skillLevel"
                      value="advanced"
                      checked={skillLevel === 'advanced'}
                      onChange={() => setSkillLevel('advanced')}
                    />
                    <span className="ccm-level-label">Advanced</span>
                  </label>
                </div>
              </div>

              <div className="ccm-form-group">
                <label className="ccm-label">
                  Practice Timeline
                  <span className="ccm-required">*</span>
                </label>
                <div className="ccm-timeframe-input">
                  <input
                    type="number"
                    className="ccm-input"
                    value={practiceTimeframe}
                    onChange={(e) => setPracticeTimeframe(e.target.value)}
                    min="1"
                    max="52"
                    required
                  />
                  <select className="ccm-select ccm-timeframe-unit">
                    <option value="weeks">weeks</option>
                  </select>
                </div>
                <p className="ccm-help-text">AI will pace problems based on your timeline</p>
              </div>

              <div className="ccm-form-group">
                <label className="ccm-label">
                  Target Companies
                  <span className="ccm-optional">Optional</span>
                </label>
                <input
                  type="text"
                  className="ccm-input"
                  value={targetCompanies}
                  onChange={(e) => setTargetCompanies(e.target.value)}
                  placeholder="e.g., Google, Meta, Amazon"
                />
                <p className="ccm-help-text">Include problems these companies frequently ask</p>
              </div>

              <div className="ccm-form-group">
                <label className="ccm-label">
                  Focus Topics
                  <span className="ccm-optional">Optional</span>
                </label>
                <input
                  type="text"
                  className="ccm-input"
                  value={focusTopics}
                  onChange={(e) => setFocusTopics(e.target.value)}
                  placeholder="e.g., Dynamic Programming, Graphs, Trees"
                />
                <p className="ccm-help-text">Emphasize specific topics in your collection</p>
              </div>

              <div className="ccm-divider">
                <div className="ccm-divider-line"></div>
                <span className="ccm-divider-text">Advanced Options</span>
                <div className="ccm-divider-line"></div>
              </div>

              <div className="ccm-advanced-section">
                  <div className="ccm-form-group">
                    <label className="ccm-label">
                      Upload Study Materials
                      <span className="ccm-optional">Optional</span>
                    </label>
                    <div 
                      className="ccm-upload-area"
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept=".pdf,.csv"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                        id="file-upload"
                      />
                      <label htmlFor="file-upload" className="ccm-upload-label">
                        <span className="ccm-upload-icon">📎</span>
                        <span className="ccm-upload-text">Click to upload or drag files here</span>
                        <span className="ccm-upload-subtext">PDF, CSV only (max 5MB)</span>
                      </label>
                    </div>
                    {uploadStatus && (
                      <p className="ccm-upload-status error">{uploadStatus}</p>
                    )}
                    {uploadedFiles.length > 0 && (
                      <div className="ccm-uploaded-files">
                        {uploadedFiles.map((file, index) => (
                          <div key={index} className="ccm-uploaded-file">
                            <span>✓ {file.name}</span>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="ccm-remove-file"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="ccm-help-text">AI will incorporate your materials into the problem set</p>
                  </div>

                  <div className="ccm-form-group">
                    <label className="ccm-label">
                      Problem Generation Options
                    </label>
                    <label className="ccm-checkbox-label">
                      <input
                        type="checkbox"
                        checked={addMoreProblems}
                        onChange={(e) => setAddMoreProblems(e.target.checked)}
                        disabled={customInstructions?.toLowerCase().includes('only')}
                      />
                      <span>Add additional problems beyond uploaded materials</span>
                    </label>
                    <p className="ccm-help-text">
                      {customInstructions?.toLowerCase().includes('only') 
                        ? "Disabled: Your instructions specify to use ONLY the uploaded problems"
                        : "When unchecked, AI will only use problems from your uploaded documents"}
                    </p>
                  </div>

                  <div className="ccm-form-group">
                    <label className="ccm-label">
                      Custom Instructions
                      <span className="ccm-optional">Optional</span>
                    </label>
                    <textarea
                      className="ccm-textarea"
                      value={customInstructions}
                      onChange={(e) => setCustomInstructions(e.target.value)}
                      placeholder="e.g., 'Use ONLY problems from uploaded CSV', 'Focus on dynamic programming', 'Include problems asked by FAANG in 2024'"
                      rows={3}
                    />
                    <p className="ccm-help-text">Provide specific instructions for AI to follow when generating your collection</p>
                  </div>
                </div>
            </form>
          )}
        </div>

        <div className="ccm-footer">
          <button type="button" onClick={onClose} className="ccm-btn ccm-btn-cancel">
            Cancel
          </button>
          {activeTab === 'manual' ? (
            <button
              onClick={handleManualSubmit}
              className="ccm-btn ccm-btn-primary"
              disabled={!name.trim()}
            >
              <span>Create Collection</span>
              <span>→</span>
            </button>
          ) : (
            <button
              onClick={handleAISubmit}
              className="ccm-btn ccm-btn-ai"
            >
              <span>✨</span>
              <span>Generate with AI</span>
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};